import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { workoutService } from '../services';

const Summary = () => {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);

  useEffect(() => {
    loadWorkout();
    // eslint-disable-next-line
  }, [logId]);

  const loadWorkout = async () => {
    try {
      const response = await workoutService.get(logId);
      setWorkout(response.data);
    } catch (error) {
      console.error('Error loading workout:', error);
    }
  };

  // Generar recomendaciones basadas en RPE promedio
  const getRecommendation = (series) => {
    const avgRPE = series.reduce((sum, s) => sum + s.intensity, 0) / series.length;
    const lastSeries = series[series.length - 1];
    
    if (avgRPE <= 3) {
      return {
        type: 'increase',
        icon: '📈',
        color: 'bg-blue-50 border-blue-200',
        textColor: 'text-blue-800',
        title: '¡Demasiado fácil!',
        message: `Aumenta el peso en 2.5-5kg o añade 2-3 repeticiones. RPE promedio muy bajo (${avgRPE.toFixed(1)}/9).`
      };
    } else if (avgRPE <= 5) {
      return {
        type: 'increase-moderate',
        icon: '⬆️',
        color: 'bg-green-50 border-green-200',
        textColor: 'text-green-800',
        title: 'Puedes progresar',
        message: `Buen trabajo. Considera aumentar 2.5kg o 1-2 repeticiones la próxima vez. RPE promedio: ${avgRPE.toFixed(1)}/9.`
      };
    } else if (avgRPE <= 7) {
      return {
        type: 'maintain',
        icon: '✅',
        color: 'bg-emerald-50 border-emerald-200',
        textColor: 'text-emerald-800',
        title: '¡Perfecto!',
        message: `Intensidad ideal para hipertrofia. Mantén este peso y busca mejorar técnica. RPE promedio: ${avgRPE.toFixed(1)}/9.`
      };
    } else if (avgRPE <= 8) {
      return {
        type: 'careful',
        icon: '⚠️',
        color: 'bg-yellow-50 border-yellow-200',
        textColor: 'text-yellow-800',
        title: 'Intensidad alta',
        message: `Muy exigente. Si completaste todas las series, ¡excelente! Si fallaste, reduce 2.5kg. RPE promedio: ${avgRPE.toFixed(1)}/9.`
      };
    } else {
      return {
        type: 'decrease',
        icon: '⬇️',
        color: 'bg-red-50 border-red-200',
        textColor: 'text-red-800',
        title: 'Demasiado pesado',
        message: `RPE muy alto (${avgRPE.toFixed(1)}/9). Reduce 2.5-5kg para mantener buena técnica y evitar lesiones.`
      };
    }
  };

  if (!workout) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-2xl">Cargando...</div>
        </div>
      </Layout>
    );
  }

  const groupedSeries = {};
  workout.series_logs?.forEach((series) => {
    const exerciseName = series.exercise?.name;
    if (!groupedSeries[exerciseName]) {
      groupedSeries[exerciseName] = [];
    }
    groupedSeries[exerciseName].push(series);
  });

  const totalWeight = workout.series_logs?.reduce(
    (sum, series) => sum + series.actual_weight * series.actual_reps,
    0
  );

  return (
    <Layout>
      <div className="px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-lg shadow-xl p-8 text-white mb-6">
            <h1 className="text-4xl font-bold mb-4">¡Entrenamiento Completado! 🎉</h1>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm opacity-80">Tiempo Total</p>
                <p className="text-3xl font-bold">
                  {Math.floor(workout.total_time_seconds / 60)} min
                </p>
              </div>
              <div>
                <p className="text-sm opacity-80">Calorías Estimadas</p>
                <p className="text-3xl font-bold">{workout.estimated_calories} kcal</p>
              </div>
              <div>
                <p className="text-sm opacity-80">Peso Total Levantado</p>
                <p className="text-3xl font-bold">{totalWeight?.toFixed(0)} kg</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Detalles del Entrenamiento</h2>
            <div className="space-y-6">
              {Object.entries(groupedSeries).map(([exerciseName, series]) => {
                const recommendation = getRecommendation(series);
                return (
                  <div key={exerciseName} className="border-b pb-6">
                    <h3 className="text-xl font-semibold mb-3">{exerciseName}</h3>
                    
                    {/* Tabla de series */}
                    <div className="overflow-x-auto mb-4">
                      <table className="min-w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                              Serie
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                              Repeticiones
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                              Peso (kg)
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                              RPE
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {series.map((s) => (
                            <tr key={s.id} className="border-t">
                              <td className="px-4 py-2 font-medium">{s.series_number}</td>
                              <td className="px-4 py-2">{s.actual_reps}</td>
                              <td className="px-4 py-2 font-semibold">{s.actual_weight} kg</td>
                              <td className="px-4 py-2">
                                <span
                                  className={`px-2 py-1 rounded text-xs font-semibold ${
                                    s.intensity >= 7
                                      ? 'bg-red-100 text-red-800'
                                      : s.intensity >= 5
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}
                                >
                                  {s.intensity}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Recomendación */}
                    <div className={`${recommendation.color} border-2 rounded-lg p-4`}>
                      <div className="flex items-start gap-3">
                        <span className="text-3xl">{recommendation.icon}</span>
                        <div className="flex-1">
                          <h4 className={`font-bold text-lg mb-1 ${recommendation.textColor}`}>
                            {recommendation.title}
                          </h4>
                          <p className={`text-sm ${recommendation.textColor}`}>
                            {recommendation.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg"
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Summary;
